import { useEffect, useRef, useState } from "react";

import {
  QuestionIcon,
  ActiveQuestionIcon,
  SearchIcon,
} from "../../assets/icons";
import { CloseIcon } from "../../assets/accountIcons";
import { RootState } from "../../redux/store";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import EmailSupportModal from "./EmailSupportModal";
import {
  setQuery,
  fetchSupportQuestions,
  incrementPage,
} from "../../features/store/supportSlice";
import { useDispatch, useSelector } from "react-redux";

export interface AppSetting {
  id: number;
  setting: string;
  value: string;
  visible: "on" | "off"; // Based on your Laravel 'visible' => 'on' check
  created_at: string;
  updated_at: string;
}

// If you are typing the full response from the API
export type AppSettingsResponse = AppSetting[];

const Support = () => {
  const [activeTab, setActiveTab] = useState(0);

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchSupportQuestions({}));
  }, [dispatch]);

  return (
    <div className="size-full min-h-screen min-w-screen overflow-x-hidden bg-BG-light dark:bg-BG-dark max-w-full">
      <span className="hidden bp:flex p-14 pb-2 lt-2xl-semibold-toggleBG-dark dark:text-white">
        Support
      </span>
      <div className="p-4 bp:px-14 pt-6 col-12 lbp:row-12 ">
        <div className="col-4 bp:min-w-80">
          <Search />
          <Questions activeTab={activeTab} setActiveTab={setActiveTab} />
        </div>
        <ActiveQuestion activeTab={activeTab} />
        <ContactUs />
      </div>
    </div>
  );
};

export default Support;

export const MMSearch = () => {
  const dispatch = useAppDispatch();
  const { searchQuery } = useAppSelector((state: RootState) => state.support);
  const [inputValue, setInputValue] = useState(searchQuery);
  const isFirstRun = useRef(true);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      dispatch(setQuery(inputValue));
      dispatch(fetchSupportQuestions({ query: inputValue, page: 1 }));
    }, 350);

    return () => clearTimeout(delayDebounceFn);
  }, [inputValue, dispatch]);

  return (
    <div className="relative flex items-center">
      <div className="absolute right-3">
        <SearchIcon />
      </div>
      <input
        type="search"
        placeholder="Search FAQs..."
        className="w-full h-12 rounded-lg p-3 satoshi-base-semibold-tabs border border-inputBorder focus:outline-none placeholder:satoshi-base-normal-inputBorder [&::-webkit-search-cancel-button]:hidden"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
      />
    </div>
  );
};

const Search = () => {
  return (
    <div
      className="relative p-4 rounded-lg col-3"
      style={{
        backgroundImage: `url(/images/search.jpeg)`,
        backgroundPosition: "center top", // Focus on the top-center part of the image
        backgroundSize: "100% 100%", // Stretch the image height to 200% so the top 50% is visible
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="col-4 z-10">
        <div className="flex flex-col">
          <span className="lt-2xl-medium-white">Search for a question</span>
          <span className="satoshi-base-normal-main-dark">
            Type your question or search keyword.
          </span>
        </div>
        <MMSearch />
      </div>
    </div>
  );
};

interface Tabs {
  activeTab: number;
  setActiveTab: (tab: number) => void;
}

const Questions = ({ activeTab, setActiveTab }: Tabs) => {
  const dispatch = useAppDispatch();
  const { questions, isLoading, isMoreLoading, searchQuery, page, hasMore } =
    useAppSelector((state) => state.support);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    dispatch(fetchSupportQuestions({ query: searchQuery, page: nextPage }));
    dispatch(incrementPage());
  };
  if (isLoading && questions.length === 0) {
    return (
      <div className="col-2 mt-4">
        <div className="flex justify-center p-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pry-500"></div>
        </div>
      </div>
    );
  }

  if (!isLoading && questions.length === 0) {
    return (
      <div className="col-2 mt-4">
        <div className="p-10 text-center">
          <p className="text-black/70 dark:text-white font-bold">
            {searchQuery
              ? `No results found for "${searchQuery}"`
              : "No questions found. Try a different keyword or contact support."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="col-2 mt-4">
      {questions.map((item, index) => {
        const isActive = index === activeTab;
        return (
          <div
            key={item.id || index} // Use database ID as key
            className={`p-1 rounded-md mb-2 transition-all ${
              isActive
                ? "border border-pry-500 bg-pry-25"
                : "border border-transparent"
            }`}
          >
            <button
              onClick={() => setActiveTab(index)}
              className="w-full flex justify-between items-center p-2"
            >
              <span
                className={`text-sm font-medium text-start ${
                  isActive ? "text-pry-600" : "text-tabs dark:text-main-dark"
                }`}
              >
                {item.question}
              </span>
              {isActive ? <ActiveQuestionIcon /> : <QuestionIcon />}
            </button>
          </div>
        );
      })}

      {hasMore && (
        <div className="p-2 grid place-content-center">
          <button
            onClick={handleLoadMore}
            disabled={isMoreLoading}
            className="flex items-center gap-2 rounded-lg border border-border-light dark:border-main-light py-2 px-4 bg-BG-light dark:bg-BG-dark satoshi-sm-bold-main-light dark:text-white disabled:opacity-70"
          >
            {isMoreLoading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
            )}
            {isMoreLoading ? "Loading..." : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
};

const ActiveQuestion = ({ activeTab }: { activeTab: number }) => {
  const questions = useAppSelector(
    (state: RootState) => state.support.questions,
  );

  const questionItem = questions[activeTab];

  if (!questionItem) {
    return (
      <div className="border border-dashed border-border-border1 dark:border-main-light rounded-lg p-6 col-6 h-fit text-center">
        <p className="satoshi-base-normal-main-light">
          Select a question to view the answer.
        </p>
      </div>
    );
  }

  const formattedDate = new Date(questionItem.created_at).toLocaleDateString(
    "en-GB",
    {
      day: "numeric",
      month: "short",
      year: "numeric",
    },
  );

  return (
    <div className="border border-border-border1 dark:border-main-light dark:bg-BG-dark rounded-lg p-6 col-6 h-fit">
      <div className="mb-4">
        <h3 className="lt-xl-semibold-pry-900 dark:text-white block mb-1">
          {questionItem.question}
        </h3>
        <p className="satoshi-base-medium-inputBorder text-xs">
          Last updated: {formattedDate}
        </p>
      </div>
      <div
        className="satoshi-base-normal-main-light dark:text-main-dark leading-relaxed"
        dangerouslySetInnerHTML={{ __html: questionItem.answer }}
      />
    </div>
  );
};

const ContactUs = () => {
  const [open, setOpen] = useState(true);
  const [showEmailModal, setShowEmailModal] = useState(false);

  const app: AppSettingsResponse = useSelector(
    (state: RootState) => state.appData.data.settings,
  );
  const whatsappSetting = app.find(
    (setting) => setting.setting === "contact_whatsapp_number",
  );

  const phoneNumber = whatsappSetting?.value || "";
  const cleanNumber = phoneNumber.replace(/\D/g, "");
  const message = encodeURIComponent("Hello! I need help with...");
  const whatsappUrl = `https://wa.me/${cleanNumber}?text=${message}`;
  const isWhatsAppVisible =
    whatsappSetting?.visible === "on" && cleanNumber.length > 0;

  if (!open) return null;
  return (
    <>
      <div className="relative bp:min-w-72 rounded-lg px-4 py-5 col-4 bg-BG-light dark:bg-BG-dark h-fit">
        <div className="col-1">
          <span className="inter-sm-semibold-toggleBG-dark dark:text-white">
            Do you still need our help?
          </span>
          <span className="inter-sm-normal-main-light dark:text-main-dark">
            Choose your preferred contact method below.
          </span>
        </div>
        <div className="flex flex-col gap-3">
          <button
            onClick={() => setShowEmailModal(true)}
            className="w-full rounded-lg px-4 py-2 bg-gradient-to-b from-pry-500 to-pry-600 satoshi-sm-bold-white"
          >
            Email us
          </button>

          {isWhatsAppVisible && (
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full rounded-lg px-4 py-2 bg-[#25D366] hover:bg-[#20ba5a] text-white satoshi-sm-bold-white flex items-center justify-center gap-2 transition-colors"
            >
              Chat on WhatsApp
            </a>
          )}
        </div>
        <button
          className="absolute top-4 right-4"
          onClick={() => setOpen(false)}
        >
          <CloseIcon />
        </button>
      </div>
      <EmailSupportModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
      />
    </>
  );
};
